/**
 * ResponsiveDialog — one dialog that adapts to viewport.
 *
 * Renders a centered Modal at desktop widths and a bottom-sheet Drawer on
 * mobile, so callers stop hand-rolling `isMobile ? Drawer : Modal`. For the
 * split media/content layout use `SplitModal` instead.
 *
 * Footer buttons: the desktop Modal footer is a right-aligned row (use natural
 * width) while the mobile Drawer footer stacks. Give footer buttons
 * `w-full md:w-auto` so they fill the sheet on mobile but stay natural-width on
 * wider screens. Put the primary button first in the DOM (top of the mobile
 * stack) and use responsive order classes to flip it to the right on desktop.
 *
 * Closing from the footer: the footer can render inside either a Modal or a
 * Drawer, so do NOT use `ModalClose`/`DrawerClose` there. Control `open` and
 * close from a plain `onClick={() => onOpenChange(false)}`.
 */
import type { ModalProps } from "@replohq/lattice/components/modal/Modal";

import * as React from "react";

import { Drawer } from "@replohq/lattice/components/drawer/Drawer";
import { Modal } from "@replohq/lattice/components/modal/Modal";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";

interface ResponsiveDialogProps extends Omit<ModalProps, "onOpenChange"> {
  onOpenChange?: (open: boolean) => void;
  contentProps?: {
    style?: React.CSSProperties;
    "data-agent-theme"?: string;
    "data-replo-surface"?: string;
  };
}

function ResponsiveDialog({
  title,
  accessibleTitle,
  description,
  trigger,
  footer,
  children,
  open,
  onOpenChange,
  dismissable = true,
  showCloseButton = true,
  size,
  className,
  UNSAFE_className,
  overlayUNSAFE_className,
  contentProps,
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        accessibleTitle={accessibleTitle}
        description={description}
        trigger={trigger}
        footer={footer}
        dismissable={dismissable}
        contentProps={contentProps}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      accessibleTitle={accessibleTitle}
      description={description}
      trigger={trigger}
      footer={footer}
      open={open}
      onOpenChange={onOpenChange}
      dismissable={dismissable}
      showCloseButton={showCloseButton}
      size={size}
      className={className}
      UNSAFE_className={UNSAFE_className}
      overlayUNSAFE_className={overlayUNSAFE_className}
    >
      {children}
    </Modal>
  );
}

export { ResponsiveDialog };
